import { can, type MeDto } from '@af/shared';
import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@/components/ui/button';
import {
  IconBell,
  IconChevronLeft,
  IconInbox,
  IconPanelRight,
  IconSearch,
  IconSidebar,
  IconSparkle,
} from '@/components/ui/icons';
import { useRole } from '@/lib/permissions';
import { IS_MAC, MOD } from '@/lib/shortcuts';
import { cn } from '@/lib/utils';
import { KillSwitch } from './KillSwitch';
import { titleKeyFor } from './nav';
import { UserMenu } from './UserMenu';

/** Quiet ghost button for links in the top bar and cards. */
export const pillCls =
  'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-fg-2 transition-colors hover:bg-hover hover:text-fg';
const iconLinkCls =
  'relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-fg-2 transition-colors hover:bg-hover hover:text-fg';

export function Keycap({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded-md border border-border bg-panel px-1 font-sans text-[11px] text-muted">
      {children}
    </kbd>
  );
}

/** ≈56px top bar: sidebar toggle + page title, centered search (opens the palette), pills and icons. */
export function TopBar({
  me,
  halted,
  unread,
  pendingApprovals,
  onSidebar,
  onBack,
  onPanel,
  panelOpen,
  panelAvailable,
  onPalette,
  onAsk,
  helpLauncher,
}: {
  helpLauncher?: ReactNode;
  me: MeDto;
  halted: boolean;
  unread?: number;
  pendingApprovals?: number;
  onSidebar: () => void;
  /** Phone: leave a detail screen (conversation, new chat) for its list. */
  onBack?: () => void;
  onPanel: () => void;
  panelOpen: boolean;
  panelAvailable: boolean;
  onPalette: () => void;
  onAsk: () => void;
}) {
  const { t } = useTranslation();
  const role = useRole();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <header className="flex min-h-14 shrink-0 flex-wrap items-center max-lg:flex-nowrap gap-2 border-b border-border px-2 py-1 sm:gap-3 sm:px-4">
      {/* Actions wrap when enlarged text or a narrow viewport needs another row. */}
      <div className="flex min-w-0 flex-1 basis-0 items-center gap-2">
        {onBack ? (
          <IconButton label={t('common.back')} onClick={onBack} className="h-8 w-8 lg:hidden">
            <IconChevronLeft size={20} />
          </IconButton>
        ) : null}
        <IconButton label={t('shell.toggleSidebar')} onClick={onSidebar} className="h-8 w-8 max-lg:hidden">
          <IconSidebar size={18} />
        </IconButton>
        <span className="truncate text-base font-medium text-fg">{t(titleKeyFor(pathname))}</span>
      </div>

      <button
        type="button"
        onClick={onPalette}
        aria-label={t('shell.searchLabel')}
        aria-keyshortcuts={IS_MAC ? 'Meta+K' : 'Control+K'}
        className="hidden h-9 w-full max-w-[380px] min-w-[200px] shrink items-center gap-2 rounded-lg border border-border bg-panel px-3 text-left text-sm text-muted transition-colors hover:border-border-strong md:flex"
      >
        <IconSearch size={16} className="shrink-0" />
        <span className="min-w-0 flex-1 truncate">{t('shell.search')}</span>
        <span aria-hidden className="flex shrink-0 items-center gap-1">
          <Keycap>{MOD}</Keycap>
          <Keycap>K</Keycap>
        </span>
      </button>

      <div className="flex min-w-0 flex-1 basis-auto flex-wrap items-center justify-end gap-1.5 max-lg:flex-nowrap">
        <KillSwitch halted={halted} />
        {/* The Tasks page has its own New task button in its header. */}
        {can(role, 'task:create') && pathname !== '/tasks' ? (
          <Link
            to="/tasks"
            search={{ new: true }}
            className={cn(
              pillCls,
              'hidden bg-cta text-cta-fg hover:bg-cta-hover hover:text-cta-fg xl:inline-flex',
            )}
          >
            {t('shell.newTask')}
          </Link>
        ) : null}
        {can(role, 'conversation:create') ? (
          <button
            type="button"
            onClick={onAsk}
            aria-keyshortcuts={IS_MAC ? 'Meta+J' : 'Control+J'}
            title={`${t('shell.askAgent')} (${MOD} J)`}
            className={cn(pillCls, 'hidden lg:inline-flex')}
          >
            <IconSparkle size={15} className="text-accent" />
            {t('shell.askAgent')}
            <span aria-hidden className="ml-0.5 hidden items-center gap-0.5 xl:flex">
              <Keycap>{MOD}</Keycap>
              <Keycap>J</Keycap>
            </span>
          </button>
        ) : null}
        <IconButton label={t('shell.searchLabel')} onClick={onPalette} className="h-8 w-8 md:hidden">
          <IconSearch size={17} />
        </IconButton>
        {can(role, 'approval:read') ? (
          <Link
            to="/approvals"
            aria-label={`${t('shell.inbox')}${pendingApprovals ? ` (${pendingApprovals})` : ''}`}
            title={t('shell.inbox')}
            className={cn(iconLinkCls, 'hidden sm:inline-flex')}
          >
            <IconInbox size={16} />
            {pendingApprovals ? (
              <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-warn px-1 font-mono text-[10px] font-semibold text-bg">
                {pendingApprovals > 9 ? '9+' : pendingApprovals}
              </span>
            ) : null}
          </Link>
        ) : null}
        <Link
          to="/notifications"
          aria-label={`${t('nav.notifications')}${unread ? ` (${unread})` : ''}`}
          title={t('nav.notifications')}
          className={iconLinkCls}
        >
          <IconBell size={16} />
          {unread ? (
            <span className="absolute top-1 right-1.5 h-2 w-2 rounded-full bg-accent ring-2 ring-bg" />
          ) : null}
        </Link>
        {panelAvailable ? (
          <IconButton
            label={panelOpen ? t('shell.hidePanel') : t('shell.showPanel')}
            onClick={onPanel}
            active={panelOpen}
            className="hidden h-8 w-8 sm:inline-flex"
          >
            <IconPanelRight size={17} />
          </IconButton>
        ) : null}
        {helpLauncher}
        <UserMenu me={me} />
      </div>
    </header>
  );
}
